/**
 * useToast — kleines Toast/Notification-System.
 * Singleton-State (reactive), kein Provider/Inject nötig.
 * ToastHost rendert die Items, jeder useToast()-Call sieht denselben Stack.
 */
import { reactive, readonly } from 'vue'
import type { DeepReadonly } from 'vue'

export type ToastVariant = 'info' | 'success' | 'error'

export interface ToastItem {
  readonly id: number
  readonly message: string
  readonly variant: ToastVariant
  readonly detail?: string
}

const items = reactive<ToastItem[]>([])
const timers = new Map<number, number>()
let counter = 0

const DEFAULT_TTL: Record<ToastVariant, number> = {
  info: 3000,
  success: 2500,
  error: 6000,
}

function show(
  message: string,
  variant: ToastVariant = 'info',
  options: { detail?: string; ttl?: number } = {},
): number {
  const id = ++counter
  const item: ToastItem = { id, message, variant, detail: options.detail }
  items.push(item)
  const ttl = options.ttl ?? DEFAULT_TTL[variant]
  if (ttl > 0) {
    timers.set(
      id,
      window.setTimeout(() => dismiss(id), ttl),
    )
  }
  return id
}

function dismiss(id: number): void {
  const idx = items.findIndex((i) => i.id === id)
  if (idx !== -1) items.splice(idx, 1)
  const t = timers.get(id)
  if (t !== undefined) {
    window.clearTimeout(t)
    timers.delete(id)
  }
}

/**
 * Rueckgabe-Typ explizit, weil `composite: true` verlangt, dass jeder
 * exportierte Typ benennbar ist — der abgeleitete Typ von readonly(reactive)
 * ist es nicht.
 */
export interface ToastApi {
  items: DeepReadonly<ToastItem[]>
  show: typeof show
  info: (m: string, detail?: string) => number
  success: (m: string, detail?: string) => number
  error: (m: string, detail?: string) => number
  dismiss: typeof dismiss
}

export function useToast(): ToastApi {
  return {
    items: readonly(items),
    show,
    info: (m: string, detail?: string) => show(m, 'info', { detail }),
    success: (m: string, detail?: string) => show(m, 'success', { detail }),
    error: (m: string, detail?: string) => show(m, 'error', { detail }),
    dismiss,
  }
}
